import type { DialogRef } from '@angular/cdk/dialog';
import { Injectable, inject, signal } from '@angular/core';

import { SnackBarService } from '@gde-mit/angular-design-system';

import { KtDsConfirmDialog } from '@shared/design-system/components/dialogs/kt-ds-confirm-dialog/kt-ds-confirm-dialog';

import { ConfirmDialogService } from '@shared/services/confirm-dialog.service';

import { TranslocoService } from '@jsverse/transloco';

import { firstValueFrom } from 'rxjs';

import { DOCUMENT_ATTACH_I18N_SCOPE } from '../../constants/document-attach-i18n';
import {
  DOCUMENT_ATTACH_DELETE_CONFIRM_DIALOG_TEST_ID,
  DOCUMENT_ATTACH_LIBRARY_ERROR_DELETE_TEST_ID,
  DOCUMENT_ATTACH_LIBRARY_ERROR_USAGE_TEST_ID,
} from '../../constants/document-attach-test-ids';
import { DocumentsApiService } from '../api/documents.api.service';

const CONFIRM_CLOSE_TIMEOUT_MS = 30_000;

const DELETE_I18N_PREFIX = `${DOCUMENT_ATTACH_I18N_SCOPE}.DOCUMENT_LIBRARY`;
const LIST_DELETE_I18N_PREFIX = 'DOCUMENT_LIBRARY.LIST';

export type DocumentLibraryDeleteManyItem = {
  readonly id: string;
  readonly fileName: string;
};

export type DocumentLibraryDeleteManyOptions = {
  readonly items: readonly DocumentLibraryDeleteManyItem[];
  readonly onDeleted?: () => Promise<void> | void;
};

export type DocumentLibraryDeleteOptions = {
  readonly id: string;
  readonly fileName: string;
  /** Runs after a successful API delete (e.g. reload list, drop selection). */
  readonly onDeleted?: () => Promise<void> | void;
};

@Injectable({ providedIn: 'root' })
export class DocumentLibraryDeleteService {
  readonly #confirmDialog = inject(ConfirmDialogService);
  readonly #documentsApi = inject(DocumentsApiService);
  readonly #snackBar = inject(SnackBarService);
  readonly #transloco = inject(TranslocoService);

  readonly deletingIds = signal<ReadonlySet<string>>(new Set());

  /** Overridable in tests so confirm-close races resolve without a long wait. */
  confirmCloseTimeoutMs = CONFIRM_CLOSE_TIMEOUT_MS;

  isDeleting(id: string): boolean {
    return this.deletingIds().has(id);
  }

  async deleteManyWithConfirmation(options: DocumentLibraryDeleteManyOptions): Promise<void> {
    const items = options.items.filter(({ id }) => id && !this.deletingIds().has(id));
    if (items.length === 0) {
      return;
    }

    const ids = items.map(({ id }) => id);
    this.#setDeletingMany(ids, true);
    try {
      const usageResults = await Promise.allSettled(items.map(({ id }) => this.#documentsApi.getActivityUsage(id)));
      if (usageResults.some((result) => result.status === 'rejected')) {
        this.#openUsageErrorSnackBar();
        return;
      }

      const totalUsage = usageResults.reduce(
        (sum, result) => sum + (result.status === 'fulfilled' ? result.value.activityCount : 0),
        0,
      );
      const count = items.length;
      const message =
        totalUsage > 0
          ? this.#transloco.translate(`${LIST_DELETE_I18N_PREFIX}.BULK_DELETE_CONFIRM.MESSAGE_WITH_USAGE`, {
              count,
              totalUsage,
            })
          : this.#transloco.translate(`${LIST_DELETE_I18N_PREFIX}.BULK_DELETE_CONFIRM.MESSAGE`, { count });

      const acceptLabel = this.#transloco.translate(`${LIST_DELETE_I18N_PREFIX}.BULK_DELETE_CONFIRM.YES`);
      const { dialogRef: ref } = this.#confirmDialog.open({
        testId: DOCUMENT_ATTACH_DELETE_CONFIRM_DIALOG_TEST_ID,
        title: acceptLabel,
        description: message,
        cancelLabel: this.#transloco.translate(`${LIST_DELETE_I18N_PREFIX}.BULK_DELETE_CONFIRM.NO`),
        confirmLabel: acceptLabel,
      });

      if (!(await this.#awaitConfirmClose(ref))) {
        return;
      }

      const deleteResults = await Promise.allSettled(items.map(({ id }) => this.#documentsApi.delete(id)));
      const anySucceeded = deleteResults.some((result) => result.status === 'fulfilled');
      const anyFailed = deleteResults.some((result) => result.status === 'rejected');
      if (anyFailed) {
        this.#snackBar.open(
          {
            message: this.#transloco.translate(`${LIST_DELETE_I18N_PREFIX}.ERROR_BULK_DELETE_PARTIAL`),
            testId: 'document-library-list-error-bulk-delete-partial',
          },
          { duration: 'short' },
        );
      }
      if (anySucceeded) {
        await options.onDeleted?.();
      }
    } catch {
      this.#openUsageErrorSnackBar();
    } finally {
      this.#setDeletingMany(ids, false);
    }
  }

  async deleteWithConfirmation(options: DocumentLibraryDeleteOptions): Promise<void> {
    const { id, fileName, onDeleted } = options;
    if (!id || this.deletingIds().has(id)) {
      return;
    }

    this.#setDeleting(id, true);
    try {
      let activityCount: number;
      try {
        ({ activityCount } = await this.#documentsApi.getActivityUsage(id));
      } catch {
        this.#openAttachUsageErrorSnackBar();
        return;
      }

      const message =
        activityCount > 0
          ? this.#transloco.translate(`${DELETE_I18N_PREFIX}.DELETE_CONFIRM.MESSAGE_WITH_USAGE`, {
              name: fileName,
              count: activityCount,
            })
          : this.#transloco.translate(`${DELETE_I18N_PREFIX}.DELETE_CONFIRM.MESSAGE`, { name: fileName });

      const acceptLabel = this.#transloco.translate(`${DELETE_I18N_PREFIX}.DELETE_CONFIRM.YES`);
      const { dialogRef: ref } = this.#confirmDialog.open({
        testId: DOCUMENT_ATTACH_DELETE_CONFIRM_DIALOG_TEST_ID,
        title: acceptLabel,
        description: message,
        cancelLabel: this.#transloco.translate(`${DELETE_I18N_PREFIX}.DELETE_CONFIRM.NO`),
        confirmLabel: acceptLabel,
      });

      if (await this.#awaitConfirmClose(ref)) {
        await this.#executeDelete(id, onDeleted);
      }
    } catch {
      this.#openAttachUsageErrorSnackBar();
    } finally {
      this.#setDeleting(id, false);
    }
  }

  #openAttachUsageErrorSnackBar(): void {
    this.#snackBar.open(
      {
        message: this.#transloco.translate(`${DELETE_I18N_PREFIX}.ERROR_USAGE`),
        testId: DOCUMENT_ATTACH_LIBRARY_ERROR_USAGE_TEST_ID,
      },
      { duration: 'short' },
    );
  }

  #openUsageErrorSnackBar(): void {
    this.#snackBar.open(
      {
        message: this.#transloco.translate(`${LIST_DELETE_I18N_PREFIX}.ERROR_USAGE`),
        testId: 'document-library-list-error-usage',
      },
      { duration: 'short' },
    );
  }

  async #executeDelete(id: string, onDeleted?: () => Promise<void> | void): Promise<void> {
    this.#setDeleting(id, true);
    try {
      await this.#documentsApi.delete(id);
      await onDeleted?.();
    } catch {
      this.#snackBar.open(
        {
          message: this.#transloco.translate(`${DELETE_I18N_PREFIX}.ERROR_DELETE`),
          testId: DOCUMENT_ATTACH_LIBRARY_ERROR_DELETE_TEST_ID,
        },
        { duration: 'short' },
      );
    } finally {
      this.#setDeleting(id, false);
    }
  }

  async #awaitConfirmClose(ref: DialogRef<boolean, KtDsConfirmDialog>): Promise<boolean> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), this.confirmCloseTimeoutMs);
    });

    try {
      const confirmed = await Promise.race([firstValueFrom(ref.closed, { defaultValue: false }), timeout]);
      return confirmed === true;
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  #setDeleting(id: string, deleting: boolean): void {
    this.#setDeletingMany([id], deleting);
  }

  #setDeletingMany(ids: readonly string[], deleting: boolean): void {
    const next = new Set(this.deletingIds());
    for (const id of ids) {
      if (deleting) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    this.deletingIds.set(next);
  }
}
